// Green VTC — Google Maps helpers, custom autocomplete, price estimation, route map
(function () {
  window.VTC = window.VTC || {};

  const VTC = window.VTC;
  VTC.config = VTC.config || {};
  VTC._mapsReady = VTC._mapsReady || null;

  VTC.loadMaps = function () {
    if (VTC._mapsReady) return VTC._mapsReady;
    VTC._mapsReady = new Promise(function (resolve, reject) {
      if (window.google && window.google.maps) return resolve(window.google);
      const key = VTC.config.googleMapsApiKey;
      if (!key) {
        console.warn('[VTC] Google Maps API key not set');
        return reject(new Error('no-key'));
      }
      window.__vtcMapsCb = function () { resolve(window.google); };
      const s = document.createElement('script');
      s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) + '&libraries=places&language=' + (VTC.config.lang || 'fr') + '&loading=async&callback=__vtcMapsCb';
      s.async = true;
      s.defer = true;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return VTC._mapsReady;
  };

  // Custom autocomplete using the new Places Data API (works for post-March-2025 accounts)
  VTC.attachAutocomplete = function (input) {
    if (!input || input.dataset.vtcAuto) return;
    input.dataset.vtcAuto = '1';
    input.setAttribute('autocomplete', 'off');

    // Build dropdown container
    const wrap = document.createElement('div');
    wrap.className = 'vtc-ac-wrap';
    input.parentNode.insertBefore(wrap, input);
    wrap.appendChild(input);
    const list = document.createElement('ul');
    list.className = 'vtc-ac-list';
    list.hidden = true;
    wrap.appendChild(list);

    let sessionToken = null;
    let debounceTimer = null;
    let activeIdx = -1;
    let currentSuggestions = [];

    function closeList() { list.hidden = true; list.innerHTML = ''; activeIdx = -1; currentSuggestions = []; }
    function renderList(suggestions) {
      list.innerHTML = '';
      currentSuggestions = suggestions;
      suggestions.forEach(function (s, i) {
        const li = document.createElement('li');
        li.className = 'vtc-ac-item';
        li.dataset.idx = i;
        const p = s.placePrediction;
        const main = (p.mainText && p.mainText.text) || p.text.text;
        const secondary = (p.secondaryText && p.secondaryText.text) || '';
        li.innerHTML = '<span class="vtc-ac-main">' + main + '</span><span class="vtc-ac-sec">' + secondary + '</span>';
        li.addEventListener('mousedown', function (ev) { ev.preventDefault(); selectSuggestion(i); });
        list.appendChild(li);
      });
      list.hidden = suggestions.length === 0;
    }

    async function selectSuggestion(i) {
      const s = currentSuggestions[i]; if (!s) return;
      const place = s.placePrediction.toPlace();
      await place.fetchFields({ fields: ['formattedAddress', 'displayName', 'location'] });
      input.value = place.formattedAddress || place.displayName || input.value;
      input.dataset.lat = place.location ? place.location.lat() : '';
      input.dataset.lng = place.location ? place.location.lng() : '';
      closeList();
      sessionToken = null;
      input.dispatchEvent(new Event('vtc:place', { bubbles: true }));
    }

    async function fetchSuggestions(q) {
      try {
        const g = await VTC.loadMaps();
        const { AutocompleteSessionToken, AutocompleteSuggestion } = await g.maps.importLibrary('places');
        if (!sessionToken) sessionToken = new AutocompleteSessionToken();
        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: q,
          sessionToken: sessionToken,
          includedRegionCodes: ['fr'],
          language: VTC.config.lang || 'fr'
        });
        renderList(suggestions || []);
      } catch (e) { console.warn('[VTC] predictions failed', e); closeList(); }
    }

    input.addEventListener('input', function () {
      const q = input.value.trim();
      clearTimeout(debounceTimer);
      if (q.length < 3) { closeList(); return; }
      debounceTimer = setTimeout(function () { fetchSuggestions(q); }, 200);
    });

    input.addEventListener('keydown', function (ev) {
      if (list.hidden) return;
      const items = list.querySelectorAll('.vtc-ac-item');
      if (ev.key === 'ArrowDown') { ev.preventDefault(); activeIdx = Math.min(activeIdx + 1, items.length - 1); updateActive(items); }
      else if (ev.key === 'ArrowUp') { ev.preventDefault(); activeIdx = Math.max(activeIdx - 1, 0); updateActive(items); }
      else if (ev.key === 'Enter' && activeIdx > -1) { ev.preventDefault(); selectSuggestion(activeIdx); }
      else if (ev.key === 'Escape') { closeList(); }
    });
    function updateActive(items) {
      items.forEach(function (el, i) { el.classList.toggle('is-active', i === activeIdx); });
    }

    document.addEventListener('click', function (ev) {
      if (!wrap.contains(ev.target)) closeList();
    });
  };

  VTC.getRoute = function (origin, destination) {
    return VTC.loadMaps().then(function (g) {
      return new Promise(function (resolve, reject) {
        const ds = new g.maps.DirectionsService();
        ds.route({
          origin: origin,
          destination: destination,
          travelMode: g.maps.TravelMode.DRIVING
        }, function (res, status) {
          if (status !== 'OK' || !res.routes[0]) return reject(new Error(status));
          const leg = res.routes[0].legs[0];
          resolve({ km: leg.distance.value / 1000, minutes: leg.duration.value / 60, result: res });
        });
      });
    });
  };

  VTC.computeFare = function (km, minutes) {
    const c = VTC.config;
    const pKm = parseFloat(c.pricePerKm) || 2.4;
    const pH = parseFloat(c.pricePerHour) || 23;
    const min = parseFloat(c.priceMin) || 15;
    const raw = km * pKm + (minutes / 60) * pH;
    return Math.max(raw, min);
  };

  VTC.estimate = function (pickup, destination) {
    const base = VTC.config.baseAddress;
    return Promise.all([
      VTC.getRoute(base, pickup),
      VTC.getRoute(pickup, destination)
    ]).then(function (results) {
      const approach = results[0];
      const trip = results[1];
      const approachCost = VTC.computeFare(approach.km, approach.minutes);
      const tripCost = VTC.computeFare(trip.km, trip.minutes);
      const total = Math.max(approachCost + tripCost, parseFloat(VTC.config.priceMin) || 15);
      return {
        approach: approach,
        trip: trip,
        approachCost: approachCost,
        tripCost: tripCost,
        total: total,
        low: Math.round(total),
        high: Math.round(total) + 5
      };
    });
  };

  // Fake driver positions around Tours (give users the sense of available drivers)
  VTC._driverPositions = [
    { lat: 47.3945, lng: 0.6890 },
    { lat: 47.3810, lng: 0.6750 },
    { lat: 47.4050, lng: 0.7020 },
    { lat: 47.3880, lng: 0.7180 },
    { lat: 47.4110, lng: 0.6680 },
    { lat: 47.3720, lng: 0.6930 }
  ];

  VTC._carSvg = function (color) {
    const c = color || '#121212';
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">' +
        '<circle cx="20" cy="20" r="18" fill="white" stroke="' + c + '" stroke-width="2"/>' +
        '<path d="M8 22l2-6c.3-.9 1.2-1.5 2.1-1.5h15.8c.9 0 1.8.6 2.1 1.5l2 6c.8.2 1.5 1 1.5 1.8V26c0 .6-.4 1-1 1h-1c-.6 0-1-.4-1-1h-19c0 .6-.4 1-1 1h-1c-.6 0-1-.4-1-1v-2.2c0-.8.7-1.6 1.5-1.8zm3.4-5 -.9 3.5h18.9l-.9-3.5c-.1-.3-.3-.5-.6-.5H12c-.3 0-.5.2-.6.5zM11 24c-.6 0-1 .4-1 1s.4 1 1 1 1-.4 1-1-.4-1-1-1zm18 0c-.6 0-1 .4-1 1s.4 1 1 1 1-.4 1-1-.4-1-1-1z" fill="' + c + '"/>' +
      '</svg>'
    );
  };

  VTC.renderDriversMap = function (container) {
    if (!container) return Promise.resolve();
    return VTC.loadMaps().then(function (g) {
      container.innerHTML = '';
      const map = new g.maps.Map(container, {
        zoom: 13,
        center: { lat: 47.3945, lng: 0.6890 },
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'cooperative',
        styles: [
          { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] }
        ]
      });
      const iconUrl = VTC.config.carMarkerUrl || VTC._carSvg('#121212');
      const icon = { url: iconUrl, scaledSize: new g.maps.Size(44, 44), anchor: new g.maps.Point(22, 22) };
      VTC._driverPositions.forEach(function (pos) {
        new g.maps.Marker({ position: pos, map: map, icon: icon });
      });
      VTC._driversMap = map;
      return map;
    }).catch(function () {});
  };

  // Render a Google Map displaying the trip route (pickup -> destination)
  VTC.renderTripMap = function (container, pickup, destination) {
    return VTC.loadMaps().then(function (g) {
      container.innerHTML = '';
      const map = new g.maps.Map(container, {
        zoom: 10,
        center: { lat: 47.39, lng: 0.68 },
        disableDefaultUI: true,
        zoomControl: true
      });
      const renderer = new g.maps.DirectionsRenderer({
        map: map,
        suppressMarkers: false,
        polylineOptions: { strokeColor: '#2e9e5b', strokeWeight: 5, strokeOpacity: 0.9 }
      });
      const ds = new g.maps.DirectionsService();
      return new Promise(function (resolve, reject) {
        ds.route({ origin: pickup, destination: destination, travelMode: g.maps.TravelMode.DRIVING }, function (res, status) {
          if (status === 'OK') { renderer.setDirections(res); resolve(res); }
          else reject(new Error(status));
        });
      });
    });
  };
})();
